// frontend/src/LoginPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import './App.css'; // Keep base variables if needed
import { logEvent } from './utils/logEvent';

function LoginPage({ setIsAuthenticated, setAuthUsername }) {
    // Theme: follow system preference on unauth pages
    const prefersDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const [isDark, setIsDark] = useState(prefersDark);
    const navigate = useNavigate();
    // Keep theme in sync with OS preference changes
    React.useEffect(() => {
        if (!window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setIsDark(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', handler);
        else if (mq.addListener) mq.addListener(handler);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handler);
            else if (mq.removeListener) mq.removeListener(handler);
        };
    }, []);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });

            const data = await response.json();

            if (response.ok) {
                const { status, temp_token } = data;

                // Store the temporary token and username for the next step.
                // sessionStorage is used so the token is available on the new page.
                if (temp_token) {
                    sessionStorage.setItem('temp_token', temp_token);
                    sessionStorage.setItem('username_2fa', username); // Store username for display
                }

                // Navigate based on the required 2FA action.
                if (status === 'success') {
                    console.log("Login successful for trusted user:", username);
                    logEvent('login_success', { method: 'password', username }).catch(() => {});
                    try { sessionStorage.setItem('tab_session', '1'); } catch (e) { /* ignore */ }
                    setIsAuthenticated(true);
                    setAuthUsername(username);
                    navigate('/'); // Redirect to the home page
                } else 
                if (status === '2fa_required') {
                    console.log("2FA verification required. Navigating to OTP page.");
                    logEvent('login_requires_2fa', { username }).catch(() => {});
                    navigate('/verify-otp'); // Redirect to the OTP entry page
                } else if (status === '2fa_setup_required') {
                    console.log("2FA setup required. Navigating to setup page.");
                    logEvent('login_requires_2fa_setup', { username }).catch(() => {});
                    navigate('/setup-2fa'); // Redirect to the QR code setup page
                } else {
                    setError('An unexpected login status was received.');
                }

            } else {
                setError(data.error || 'Login failed. Please try again.');
                logEvent('login_failure', { username, status: response.status, error: data.error }).catch(() => {});
                setIsAuthenticated(false);
                setAuthUsername('');
            }
        } catch (err) {
            console.error("Login error:", err);
            setError('An network error occurred during login. Please check connection.');
            logEvent('login_failure', { username, error: err?.message || 'network_error' }).catch(() => {});
            setIsAuthenticated(false);
            setAuthUsername('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`login-page-container ${isDark ? 'theme-dark' : 'theme-light'}`}>
            <section className="login-hero">
                <div className="login-logo-container">
                    <img
                        src={`${process.env.PUBLIC_URL}/Pearlify-logo.png`}
                        alt="Pearlify logo"
                        className="login-logo"
                    />
                    <img
                        src={`${process.env.PUBLIC_URL}/Pearlify-text.png`}
                        alt="Pearlify"
                        className="login-wordmark"
                    />
                </div>
                <header className="login-header">
                    <h1>Welcome to Pearlify</h1>
                    <p>Please sign in to continue</p>
                </header>
            </section>

            <div className="login-card">
                <form onSubmit={handleLogin} className="login-form">
                    {error && <div className="login-error" role="alert">{error}</div>}
                    <div className="form-section">
                        <label htmlFor="login-username">Username</label>
                        <input
                            type="text"
                            id="login-username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            disabled={loading}
                            autoComplete="username"
                            placeholder="Enter your username"
                        />
                    </div>
                    <div className="form-section">
                        <label htmlFor="login-password">Password</label>
                        <input
                            type="password"
                            id="login-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                        />
                    </div>
                    <button type="submit" className="login-submit" disabled={loading || !username || !password}>
                        {loading ? 'Logging in…' : 'Login'}
                    </button>
                </form>
            </div>
            <footer className="login-footer">
                <small>Need access? Contact your administrator.</small>
            </footer>
        </div>
    );
}

export default LoginPage;
